import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronLeft, X, Sparkles } from "lucide-react";
import axios from "axios";
import { API } from "../App";

// ── Per-page step definitions ──────────────────────────────────────────

const PAGE_STEPS = {
  dashboard: [
    {
      target: null,
      titleKey: "onboarding.dashboard.welcome.title",
      contentKey: "onboarding.dashboard.welcome.content",
      position: "center",
    },
    {
      target: '[data-testid="nav-dashboard"]',
      titleKey: "onboarding.dashboard.nav.title",
      contentKey: "onboarding.dashboard.nav.content",
      position: "right",
    },
    {
      target: '[data-testid="create-project-btn"]',
      titleKey: "onboarding.dashboard.create.title",
      contentKey: "onboarding.dashboard.create.content",
      position: "bottom",
    },
    {
      target: null,
      titleKey: "onboarding.dashboard.pipeline.title",
      contentKey: "onboarding.dashboard.pipeline.content",
      position: "center",
    },
    {
      target: null,
      titleKey: "onboarding.dashboard.ready.title",
      contentKey: "onboarding.dashboard.ready.content",
      position: "center",
    },
  ],

  analysis: [
    {
      target: null,
      titleKey: "onboarding.analysis.intro.title",
      contentKey: "onboarding.analysis.intro.content",
      position: "center",
    },
    {
      target: '[data-testid="upload-zone"]',
      titleKey: "onboarding.analysis.upload.title",
      contentKey: "onboarding.analysis.upload.content",
      position: "bottom",
    },
    {
      target: '[data-testid="creative-vision-input"]',
      titleKey: "onboarding.analysis.vision.title",
      contentKey: "onboarding.analysis.vision.content",
      position: "top",
    },
    {
      target: '[data-testid="analyze-btn"]',
      titleKey: "onboarding.analysis.analyze.title",
      contentKey: "onboarding.analysis.analyze.content",
      position: "bottom",
    },
    {
      target: null,
      titleKey: "onboarding.analysis.after.title",
      contentKey: "onboarding.analysis.after.content",
      position: "center",
    },
  ],

  breakdown: [
    {
      target: null,
      titleKey: "onboarding.breakdown.intro.title",
      contentKey: "onboarding.breakdown.intro.content",
      position: "center",
    },
    {
      target: '[data-testid="show-versions-btn"]',
      titleKey: "onboarding.breakdown.versions.title",
      contentKey: "onboarding.breakdown.versions.content",
      position: "bottom",
    },
    {
      target: null,
      titleKey: "onboarding.breakdown.shots.title",
      contentKey: "onboarding.breakdown.shots.content",
      position: "center",
    },
    {
      target: '[data-testid="validate-breakdown-btn"]',
      titleKey: "onboarding.breakdown.validate.title",
      contentKey: "onboarding.breakdown.validate.content",
      position: "top",
    },
  ],

  production: [
    {
      target: null,
      titleKey: "onboarding.production.intro.title",
      contentKey: "onboarding.production.intro.content",
      position: "center",
    },
    {
      target: '[data-testid="prod-generate-prompts-btn"]',
      titleKey: "onboarding.production.prompts.title",
      contentKey: "onboarding.production.prompts.content",
      position: "bottom",
    },
    {
      target: '[data-testid="prod-generate-images-btn"]',
      titleKey: "onboarding.production.images.title",
      contentKey: "onboarding.production.images.content",
      position: "bottom",
    },
    {
      target: null,
      titleKey: "onboarding.production.modes.title",
      contentKey: "onboarding.production.modes.content",
      position: "center",
    },
    {
      target: null,
      titleKey: "onboarding.production.working.title",
      contentKey: "onboarding.production.working.content",
      position: "center",
    },
  ],

  timeline: [
    {
      target: null,
      titleKey: "onboarding.timeline.intro.title",
      contentKey: "onboarding.timeline.intro.content",
      position: "center",
    },
    {
      target: '[data-testid="tl-bin-btn"]',
      titleKey: "onboarding.timeline.bin.title",
      contentKey: "onboarding.timeline.bin.content",
      position: "bottom",
    },
    {
      target: '[data-testid="tl-toolbar"]',
      titleKey: "onboarding.timeline.toolbar.title",
      contentKey: "onboarding.timeline.toolbar.content",
      position: "bottom",
    },
    {
      target: '[data-testid="tl-playback"]',
      titleKey: "onboarding.timeline.playback.title",
      contentKey: "onboarding.timeline.playback.content",
      position: "top",
    },
    {
      target: '[data-testid="tl-export-btn"]',
      titleKey: "onboarding.timeline.export.title",
      contentKey: "onboarding.timeline.export.content",
      position: "bottom",
    },
    {
      target: null,
      titleKey: "onboarding.timeline.shortcuts.title",
      contentKey: "onboarding.timeline.shortcuts.content",
      position: "center",
    },
  ],

  toolbox: [
    {
      target: null,
      titleKey: "onboarding.toolbox.intro.title",
      contentKey: "onboarding.toolbox.intro.content",
      position: "center",
    },
    {
      target: '[data-testid="toolbox-tabs"]',
      titleKey: "onboarding.toolbox.tabs.title",
      contentKey: "onboarding.toolbox.tabs.content",
      position: "bottom",
    },
    {
      target: '[data-testid="toolbox-sidepanel"]',
      titleKey: "onboarding.toolbox.sidepanel.title",
      contentKey: "onboarding.toolbox.sidepanel.content",
      position: "center",
    },
    {
      target: '[data-testid="toolbox-bottombar"]',
      titleKey: "onboarding.toolbox.bottombar.title",
      contentKey: "onboarding.toolbox.bottombar.content",
      position: "top",
    },
    {
      target: '[data-testid="toolbox-sessions-btn"]',
      titleKey: "onboarding.toolbox.sessions.title",
      contentKey: "onboarding.toolbox.sessions.content",
      position: "bottom",
    },
    {
      target: null,
      titleKey: "onboarding.toolbox.ready.title",
      contentKey: "onboarding.toolbox.ready.content",
      position: "center",
    },
  ],
};

// Key for localStorage tracking of which pages have been toured
const TOURED_PAGES_KEY = "moodwalkrs_toured_pages";
const TOURS_DISABLED_KEY = "moodwalkrs_tours_disabled";
const TOURS_OWNER_KEY = "moodwalkrs_tours_owner";   // cf-i8vh : à quel compte appartient la copie du navigateur

// Get set of toured pages from localStorage
function getTouredPages() {
  try {
    const raw = localStorage.getItem(TOURED_PAGES_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

// Mark a page as toured in localStorage
function markPageToured(page) {
  const toured = getTouredPages();
  toured.add(page);
  localStorage.setItem(TOURED_PAGES_KEY, JSON.stringify([...toured]));
  pushAccountTours({ toured_pages_add: [page] });
}

// cf-i8vh : la visite est vue une fois par COMPTE. Le compte (users.toured_pages, lu par /auth/me) fait foi ; le
// navigateur en garde une copie, alignée au chargement de l'utilisateur AVANT l'ouverture d'une page (App.js attend
// useAccountTours), puisque chaque page décide d'ouvrir sa visite sur cette copie (shouldAutoTour).
const TOURS_PENDING_KEY = "moodwalkrs_tours_pending";   // pages vues dont l'envoi au compte a échoué (réseau)
const TOURS_RESET_PENDING_KEY = "moodwalkrs_tours_reset_pending";   // « revoir le tutoriel » pas encore reçu
let adoptedFor = null;    // compte dont la copie est alignée dans cet onglet
let unknownFor = null;    // compte qui n'a pas répondu à temps : pas de visite automatique (on ne sait pas s'il l'a vue)
let sending = Promise.resolve();   // les envois partent dans l'ordre (un reset puis un ajout ne s'inversent pas)

function readList(key) {
  try {
    const v = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

// Ajouts fusionnés côté serveur (toured_pages_add) : deux appareils ouverts ne s'effacent pas ; seul « revoir le
// tutoriel » remplace la liste. Un envoi perdu par le réseau (pas un refus du serveur) est gardé en attente et
// renvoyé au prochain chargement.
function pushAccountTours(body) {
  sending = sending.then(() => axios.put(`${API}/auth/profile`, body)).catch((err) => {
    if (err?.response && err.response.status < 500) return;
    try {                    // stockage plein ou bloqué : l'envoi est perdu, mais la file continue
      if (Array.isArray(body.toured_pages)) {
        localStorage.setItem(TOURS_RESET_PENDING_KEY, "true");
        localStorage.setItem(TOURS_PENDING_KEY, JSON.stringify(body.toured_pages));
      } else if (body.toured_pages_add) {
        const pending = new Set([...readList(TOURS_PENDING_KEY), ...body.toured_pages_add]);
        localStorage.setItem(TOURS_PENDING_KEY, JSON.stringify([...pending]));
      }
    } catch (e) {
      console.warn("tours: pending send not kept", e);
    }
  });
}

// Aligne la copie du navigateur sur le compte `me` (réponse de /auth/me). Une fois par compte et par onglet.
export function adoptAccountTours(me) {
  if (!me?.id || adoptedFor === me.id) return;
  adoptedFor = me.id;
  unknownFor = null;
  const owner = localStorage.getItem(TOURS_OWNER_KEY);
  if (owner && owner !== me.id) {                       // navigateur partagé : la copie est celle d'un autre compte
    [TOURED_PAGES_KEY, TOURS_DISABLED_KEY, TOURS_PENDING_KEY, TOURS_RESET_PENDING_KEY].forEach((k) => localStorage.removeItem(k));
  }
  localStorage.setItem(TOURS_OWNER_KEY, me.id);
  const pending = readList(TOURS_PENDING_KEY);
  const resetPending = localStorage.getItem(TOURS_RESET_PENDING_KEY) === "true";
  [TOURS_PENDING_KEY, TOURS_RESET_PENDING_KEY].forEach((k) => localStorage.removeItem(k));
  if (resetPending) {                                   // « revoir le tutoriel » fait hors ligne : il l'emporte
    localStorage.setItem(TOURED_PAGES_KEY, JSON.stringify(pending));
    localStorage.removeItem(TOURS_DISABLED_KEY);
    pushAccountTours({ toured_pages: pending, tours_disabled: false });
  } else if (Array.isArray(me.toured_pages)) {
    // copie sans propriétaire = d'avant le passage au compte : elle s'ajoute au compte au lieu d'être remplacée
    // (sinon le 2e appareil ouvert oublie ses visites) ; un « non désactivé » explicite du compte n'est jamais écrasé
    const unowned = !owner;
    const extra = unowned ? [...getTouredPages()].filter((pg) => !me.toured_pages.includes(pg)) : [];
    const adds = [...new Set([...pending, ...extra])];
    const disable = me.tours_disabled === true || (unowned && areToursDisabled() && me.tours_disabled !== false);
    localStorage.setItem(TOURED_PAGES_KEY, JSON.stringify([...new Set([...me.toured_pages, ...adds])]));
    if (disable) localStorage.setItem(TOURS_DISABLED_KEY, "true");
    else localStorage.removeItem(TOURS_DISABLED_KEY);
    const body = adds.length ? { toured_pages_add: adds } : {};
    if (disable && me.tours_disabled !== true) body.tours_disabled = true;
    if (Object.keys(body).length) pushAccountTours(body);
  } else if (getTouredPages().size || areToursDisabled() || me.tours_disabled === true) {
    if (me.tours_disabled === true) localStorage.setItem(TOURS_DISABLED_KEY, "true");
    // compte jamais renseigné : il reprend ce que ce navigateur savait (pour ce même compte) ; « non désactivé »
    // n'est jamais envoyé, il écraserait un choix que ce navigateur ignore
    const body = getTouredPages().size ? { toured_pages_add: [...getTouredPages()] } : {};
    if (areToursDisabled() && me.tours_disabled !== true) body.tours_disabled = true;
    if (Object.keys(body).length) pushAccountTours(body);
  }
}

// App.js : true quand la copie des visites est alignée sur le compte connecté (ou qu'il n'y a pas de compte). Le
// compte arrive avec la réponse de connexion ou /auth/me ; s'il n'est pas là en 1,5 s (serveur lent, ou backend sans
// ces clés après un retour arrière), la page s'affiche SANS visite automatique plutôt que d'attendre (Jimmy, APR-19).
export function useAccountTours(user) {
  const id = user?.id;
  const known = user ? user.toured_pages !== undefined : false;
  const [adopted, setAdopted] = useState(null);
  const [timedOut, setTimedOut] = useState(null);   // compte pour lequel l'attente a expiré
  useEffect(() => {
    if (!id) return undefined;
    if (known) {
      try {
        adoptAccountTours(user);
      } catch (e) {          // stockage plein ou bloqué : les pages s'ouvrent quand même, la copie du navigateur décide
        console.warn("tours: account copy not aligned", e);
      }
      setAdopted(id);
      return undefined;
    }
    const t = setTimeout(() => {
      unknownFor = id;
      setTimedOut(id);
    }, 1500);
    return () => clearTimeout(t);
  }, [id, known]); // eslint-disable-line react-hooks/exhaustive-deps
  return !id || adopted === id || timedOut === id;
}

// tests
export function forgetAccountTours() {
  adoptedFor = null;
  unknownFor = null;
}

// Permanently disable all tours for this user
export function disableAllTours() {
  localStorage.setItem(TOURS_DISABLED_KEY, "true");
  // Also mark all pages as toured for good measure
  const allPages = Object.keys(PAGE_STEPS);
  localStorage.setItem(TOURED_PAGES_KEY, JSON.stringify(allPages));
  pushAccountTours({ toured_pages_add: allPages, tours_disabled: true });
}

// Check if tours are permanently disabled
export function areToursDisabled() {
  return localStorage.getItem(TOURS_DISABLED_KEY) === "true";
}

// Reset all toured pages (for Settings reset button)
export function resetTourProgress() {
  unknownFor = null;         // relance voulue (Guide, Réglages) : la visite s'affiche même sans réponse du compte
  localStorage.removeItem(TOURED_PAGES_KEY);
  localStorage.removeItem(TOURS_DISABLED_KEY);
  pushAccountTours({ toured_pages: [], tours_disabled: false });
}

// Check if a page tour should auto-run
export function shouldAutoTour(page) {
  if (unknownFor || areToursDisabled()) return false;
  return !getTouredPages().has(page);
}

// ── Tooltip positioning ────────────────────────────────────────────────

function getTooltipStyle(targetEl, position) {
  const TOOLTIP_W = 380;
  const MARGIN = 16;

  // Téléphone (cf-374) : toujours centré — à côté d'une cible, 380 px sortaient de l'écran
  if (!targetEl || position === "center" || window.innerWidth < TOOLTIP_W + 2 * MARGIN + 200) {
    return {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  }

  const rect = targetEl.getBoundingClientRect();
  const gap = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Clamp horizontal position so tooltip stays on screen
  const clampLeft = (rawLeft) => {
    return Math.max(MARGIN, Math.min(rawLeft, vw - TOOLTIP_W - MARGIN));
  };

  switch (position) {
    case "right":
      return {
        position: "fixed",
        top: `${Math.max(MARGIN, Math.min(rect.top + rect.height / 2, vh - MARGIN))}px`,
        left: `${rect.right + gap}px`,
        transform: "translateY(-50%)",
      };
    case "bottom":
      return {
        position: "fixed",
        top: `${rect.bottom + gap}px`,
        left: `${clampLeft(rect.left + rect.width / 2 - TOOLTIP_W / 2)}px`,
      };
    case "left":
      return {
        position: "fixed",
        top: `${Math.max(MARGIN, Math.min(rect.top + rect.height / 2, vh - MARGIN))}px`,
        right: `${vw - rect.left + gap}px`,
        transform: "translateY(-50%)",
      };
    case "top":
      return {
        position: "fixed",
        bottom: `${vh - rect.top + gap}px`,
        left: `${clampLeft(rect.left + rect.width / 2 - TOOLTIP_W / 2)}px`,
      };
    default:
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
  }
}

// ── Spotlight overlay ──────────────────────────────────────────────────

// cf-d2ra : un clic / tap sur le voile = Passer (on s'attend à ce qu'il ferme la visite)
function SpotlightOverlay({ targetEl, onDismiss }) {
  if (!targetEl) {
    return (
      <div className="fixed inset-0 bg-black/70 z-[9998] transition-all duration-300" onClick={onDismiss}
        data-testid="onboarding-backdrop" />
    );
  }

  const rect = targetEl.getBoundingClientRect();
  const pad = 8;

  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      <div className="absolute bg-black/70 pointer-events-auto" onClick={onDismiss} style={{ top: 0, left: 0, right: 0, height: `${rect.top - pad}px` }} />
      <div className="absolute bg-black/70 pointer-events-auto" onClick={onDismiss} style={{ top: `${rect.bottom + pad}px`, left: 0, right: 0, bottom: 0 }} />
      <div className="absolute bg-black/70 pointer-events-auto" onClick={onDismiss} style={{ top: `${rect.top - pad}px`, left: 0, width: `${rect.left - pad}px`, height: `${rect.height + pad * 2}px` }} />
      <div className="absolute bg-black/70 pointer-events-auto" onClick={onDismiss} style={{ top: `${rect.top - pad}px`, left: `${rect.right + pad}px`, right: 0, height: `${rect.height + pad * 2}px` }} />
      <div
        className="absolute rounded-lg ring-2 ring-violet-500 ring-offset-2 ring-offset-transparent"
        style={{
          top: `${rect.top - pad}px`,
          left: `${rect.left - pad}px`,
          width: `${rect.width + pad * 2}px`,
          height: `${rect.height + pad * 2}px`,
        }}
      />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export default function OnboardingTour({ run, page = "dashboard", onComplete, onDisable }) {
  const { t } = useTranslation();
  const steps = PAGE_STEPS[page] || PAGE_STEPS.dashboard;
  const [step, setStep] = useState(0);
  const [targetEl, setTargetEl] = useState(null);
  const tooltipRef = useRef(null);
  const nextRef = useRef(null);
  const titleId = `onboarding-title-${page}`;

  const currentStep = steps[step];

  // cf-d2ra : la visite est une boîte de dialogue — le focus y entre à l'ouverture (sur « Suivant ») et revient
  // où il était à la fermeture (clavier, lecteur d'écran : la page ne paraît pas « morte »)
  useEffect(() => {
    if (!run) return undefined;
    const before = document.activeElement;
    const id = setTimeout(() => nextRef.current?.focus(), 0);
    return () => {
      clearTimeout(id);
      if (before && typeof before.focus === "function" && document.contains(before)) before.focus();
    };
  }, [run]);

  // Reset step when page changes or tour starts
  useEffect(() => {
    setStep(0);
    setTargetEl(null);
  }, [page, run]);

  // Find and track the target element
  useEffect(() => {
    if (!run) return;

    if (!currentStep.target) {
      setTargetEl(null);
      return;
    }

    const timeout = setTimeout(() => {
      const el = document.querySelector(currentStep.target);
      // une cible cachée (barre latérale repliée dans le tiroir sur téléphone) n'est pas une cible
      const r = el?.getBoundingClientRect();
      const visible = r && r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth;
      if (el && visible) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTargetEl(el);
      } else {
        setTargetEl(null);
      }
    }, 200);

    return () => clearTimeout(timeout);
  }, [run, step, currentStep.target]);

  const handleNext = useCallback(() => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      // Tour complete — mark page as toured
      markPageToured(page);
      if (onComplete) onComplete();
      setStep(0);
    }
  }, [step, steps.length, page, onComplete]);

  const handlePrev = useCallback(() => {
    if (step > 0) setStep(step - 1);
  }, [step]);

  const handleSkip = useCallback(() => {
    markPageToured(page);
    if (onComplete) onComplete();
    setStep(0);
  }, [page, onComplete]);

  const handleDisable = useCallback(() => {
    disableAllTours();
    if (onDisable) onDisable();
    if (onComplete) onComplete();
    setStep(0);
  }, [onDisable, onComplete]);

  // Keyboard navigation
  useEffect(() => {
    if (!run) return;
    const handler = (e) => {
      // Entrée sur un bouton : le bouton agit lui-même (sinon « Suivant » avançait de deux étapes)
      if (e.key === "Enter" && e.target instanceof HTMLButtonElement) return;
      if (e.key === "ArrowRight" || e.key === "Enter") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "Escape") handleSkip();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [run, handleNext, handlePrev, handleSkip]);

  if (!run) return null;

  const tooltipStyle = getTooltipStyle(targetEl, currentStep.position);
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;

  // Page label for context
  const pageLabels = {
    dashboard: t('onboarding.pageDashboard'),
    analysis: t('onboarding.pageAnalysis'),
    breakdown: t('onboarding.pageBreakdown'),
    production: t('onboarding.pageProduction'),
    timeline: t('onboarding.pageTimeline'),
    toolbox: t('onboarding.pageToolbox'),
  };

  // dans <body> (cf-374) : sous <main> (transformé par l'animation de page), « fixed » se calait sur
  // main et non sur l'écran — sur téléphone le voile couvrait tout et la bulle sortait de l'écran
  return createPortal(
    <>
      <SpotlightOverlay targetEl={targetEl} onDismiss={handleSkip} />

      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="onboarding-dialog"
        className="z-[9999] w-[380px] glass-panel border border-white/[0.08] rounded-xl shadow-2xl shadow-black/50 p-5"
        style={{ ...tooltipStyle, maxWidth: "calc(100vw - 32px)" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-violet-500/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-violet-400" />
            </div>
            <div>
              <h3 id={titleId} className="text-white font-semibold text-sm">{t(currentStep.titleKey)}</h3>
              {page !== "dashboard" && (
                <span className="text-[10px] text-neutral-500">{pageLabels[page]}</span>
              )}
            </div>
          </div>
          <button
            onClick={handleSkip}
            aria-label={t('onboarding.skip')}
            className="p-1 rounded-lg hover:bg-white/[0.06] text-neutral-500 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <p className="text-neutral-300 text-sm leading-relaxed whitespace-pre-line mb-4">
          {t(currentStep.contentKey)}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    i === step ? "bg-violet-400" : i < step ? "bg-violet-400/40" : "bg-neutral-600"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={handleDisable}
              className="text-[10px] text-neutral-600 hover:text-neutral-400 transition-colors text-left"
            >
              {t('onboarding.dontShowAgain')}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                {t('onboarding.previous')}
              </button>
            )}
            {isFirst && (
              <button
                onClick={handleSkip}
                data-testid="onboarding-skip"
                className="px-3 py-1.5 rounded-lg text-xs text-neutral-200 border border-white/20 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                {t('onboarding.skip')}
              </button>
            )}
            <button
              ref={nextRef}
              onClick={handleNext}
              data-testid="onboarding-next"
              className="flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs font-medium bg-violet-600 hover:bg-violet-500 text-white transition-colors"
            >
              {isLast ? t('onboarding.done') : t('onboarding.next')}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
